import type { ReactNode } from 'react';

/**
 * A named group of fields inside a form — "Present address", "Permanent address".
 *
 * **It carries vertical margin, and that is the whole reason it exists as a component.** A
 * modal body already spaces its children with `gap-4`, which is the gap BETWEEN FIELDS. A
 * group of fields under a heading is not another field: it is a section, and set at the same
 * rhythm as the inputs around it the legend reads as a label belonging to the control
 * directly beneath rather than as a heading over the several that follow.
 *
 * `my-3` on top of the parent's `gap-4` puts 28px either side — enough that the boundary is
 * visible without a rule, which a form this short does not need.
 *
 * The legend itself is SMALL, MUTED and UPPERCASE — chrome naming the block, not another line
 * of prose competing with the labels inside it. See the note on it below.
 *
 * **It is a real `<fieldset>` with a real `<legend>`**, so a screen reader announces the
 * group's name with every field inside it. A `<p>` heading over a `<div>` looks the same and
 * says nothing.
 *
 * Not to be confused with two neighbours:
 *  - [`<FieldGroup>`](./FieldGroup.tsx) joins segments that make up ONE value — a surname and
 *    its suffix — into a single bordered control. This groups SEVERAL values under a name.
 *  - [`<RadioGroup>`](./RadioGroup.tsx) is itself a `<fieldset>`, because a set of radios is
 *    one control. It takes no margin of its own: it sits in the run of fields, not around it.
 */
export function FormFieldset({
  legend,
  children,
  className = '',
}: {
  legend: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <fieldset className={`my-3 flex flex-col gap-4 ${className}`}>
      {/*
        SMALLER, MUTED AND UPPERCASE — the product's own group-label treatment (26 Oct 2026).

        It was `text-sm text-slate-800`, DARKER and larger than the field labels beneath it, on
        the reasoning that a section heading outranks them. That made it compete: at the same
        size as the prose around it, one more line of dark text is read as content rather than
        as chrome naming the block under it.

        `text-[10px] uppercase tracking-wider text-gray-600` is what the filter rail already
        puts over a facet's values — the same job, a name over a list of things — so this is
        the existing idiom rather than a fourth spelling of it. The product runs this treatment
        at three sizes (9px on table headers and nav suite labels, 10px on the filter rail's
        groups, 11px on `<GroupCard>`'s spine); 10px is the one whose job matches.

        **SEMIBOLD, which makes this `NAV_GROUP_LABEL` exactly, one size up** (product call,
        26 Oct 2026). The sidebar's suite labels are `text-[9px] font-semibold uppercase
        tracking-wider text-gray-600` and do the same job — a name over the things it groups —
        so the two now differ only in the size their surface calls for.

        It is four signals at once, which `<PreviewTable variant="table-display">` had stripped
        back to one. That is not a contradiction: there the `bg-slate-50` tint was ALREADY
        saying "this is the key", so the rest was repetition. A legend has no tint and no cell
        to stand in, and at 10px muted the weight is what keeps it legible rather than faint.

        `mb-2` stays because a `<legend>` is not a flex item: the fieldset's `gap-4` spaces the
        FIELDS and never reaches it, so without a margin the legend sits flush against the
        first label and the two read as a two-line heading.
      */}
      <legend className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-600">
        {legend}
      </legend>
      {children}
    </fieldset>
  );
}
